import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  celeryApi,
  CeleryStats,
  CeleryTask,
  CeleryWorker,
} from "../../api/celery";
import CeleryPage from "./CeleryPage";
import { CancelFeedback, CancelTaskResult, RerunTaskResult } from "./CeleryPage";
import { semantic, ramps, chartPalette } from "../../common/theme/brandTokens";

type TabValue =
  | "ALL"
  | "STARTED"
  | "SUCCESS"
  | "FAILURE"
  | "RETRY"
  | "PENDING"
  | "RECEIVED"
  | "REVOKED";

const STATE_COLOURS: Record<string, string> = {
  PENDING: semantic.warning.main,
  RECEIVED: semantic.info.main,
  STARTED: semantic.info.dark,
  SUCCESS: semantic.success.main,
  FAILURE: semantic.error.main,
  RETRY: chartPalette[3],
  REVOKED: ramps.neutral[400],
};

const POLL_INTERVAL = 8_000; // ms
const CANCEL_STORAGE_KEY = "celery_cancel_requests_v1";
const TERMINAL_STATES = new Set(["SUCCESS", "FAILURE", "REVOKED"]);

type CancelRequestRecord = {
  message: string;
  requestedAt: number;
};

const loadCancelRequests = (): Record<string, CancelRequestRecord> => {
  try {
    const raw = localStorage.getItem(CANCEL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const CeleryMonitorContainer: React.FC = () => {
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();
  const [stats, setStats] = useState<CeleryStats | null>(null);
  const [tasks, setTasks] = useState<CeleryTask[]>([]);
  const [workers, setWorkers] = useState<Record<string, CeleryWorker>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabValue>("ALL");
  const [search, setSearch] = useState("");
  const [selectedTask, setSelectedTask] = useState<CeleryTask | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cancelInFlightTaskId, setCancelInFlightTaskId] = useState<string | null>(null);
  const [rerunInFlightTaskId, setRerunInFlightTaskId] = useState<string | null>(null);
  const [cancelFeedback, setCancelFeedback] = useState<CancelFeedback | null>(null);
  const [cancelRequests, setCancelRequests] = useState<Record<string, CancelRequestRecord>>(loadCancelRequests);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    localStorage.setItem(CANCEL_STORAGE_KEY, JSON.stringify(cancelRequests));
  }, [cancelRequests]);

  const normalizeWorkers = (raw: unknown): Record<string, CeleryWorker> => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const objectData = raw as Record<string, unknown>;
    const nestedWorkers = objectData.workers;
    if (nestedWorkers && typeof nestedWorkers === "object" && !Array.isArray(nestedWorkers)) {
      return nestedWorkers as Record<string, CeleryWorker>;
    }
    return objectData as Record<string, CeleryWorker>;
  };

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const stateFilter = tab === "ALL" ? undefined : tab;
      const [statsRes, tasksRes, workersRes] = await Promise.allSettled([
        celeryApi.getStats(),
        celeryApi.getTasks({
          limit: 500,
          state: stateFilter,
          taskname: search || undefined,
        }),
        celeryApi.getWorkers(),
      ]);

      if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
      if (tasksRes.status === "fulfilled") {
        const latestTasks = tasksRes.value.data.tasks;
        setTasks(latestTasks);
        setCancelRequests((previous) => {
          const next = { ...previous };
          for (const task of latestTasks) {
            const state = (task.state || "").toUpperCase();
            if (TERMINAL_STATES.has(state)) {
              delete next[task.id];
            }
          }
          return next;
        });
      }
      if (workersRes.status === "fulfilled") {
        setWorkers(normalizeWorkers(workersRes.value.data));
      }

      if (
        statsRes.status === "rejected" &&
        tasksRes.status === "rejected" &&
        workersRes.status === "rejected"
      ) {
        setError("Cannot reach Celery backend – is Flower running?");
      }
    } catch {
      setError("Unexpected error fetching Celery data");
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => {
    setLoading(true);
    fetchData();

    const startPoll = () => {
      pollRef.current = setInterval(fetchData, POLL_INTERVAL);
    };
    const stopPoll = () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };

    const onVisChange = () => {
      if (document.hidden) stopPoll();
      else {
        fetchData();
        startPoll();
      }
    };

    startPoll();
    document.addEventListener("visibilitychange", onVisChange);

    return () => {
      stopPoll();
      document.removeEventListener("visibilitychange", onVisChange);
    };
  }, [fetchData]);

  const openDetail = (task: CeleryTask) => {
    setSelectedTask(task);
    setDrawerOpen(true);
    setCancelFeedback(null);
    navigate(`/celery/${task.id}`);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedTask(null);
    setCancelFeedback(null);
    navigate("/celery");
  };

  const cancelTask = useCallback(async (taskId: string): Promise<CancelTaskResult> => {
    setCancelInFlightTaskId(taskId);
    setCancelFeedback(null);
    try {
      const response = await celeryApi.cancelTask(taskId, { terminate: true, signal: "SIGTERM" });
      await fetchData();
      const message = `Cancellation requested for task ${taskId.slice(0, 8)}…`;
      setCancelFeedback({ type: "success", message });
      setCancelRequests((previous) => ({
        ...previous,
        [taskId]: {
          message,
          requestedAt: Date.now(),
        },
      }));
      return {
        ok: true,
        message,
        statusCode: response.status,
      };
    } catch (error: any) {
      const statusCode: number | undefined = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message || "Unknown error";
      const message = `Cancel failed (${statusCode ?? "no-status"}): ${detail}`;
      setCancelFeedback({ type: "error", message });
      return {
        ok: false,
        message,
        statusCode,
      };
    } finally {
      setCancelInFlightTaskId(null);
    }
  }, [fetchData]);

  const rerunTask = useCallback(async (taskId: string): Promise<RerunTaskResult> => {
    setRerunInFlightTaskId(taskId);
    setCancelFeedback(null);
    try {
      const response = await celeryApi.rerunTask(taskId);
      const newId = response.data.new_task_id;
      const message = `Task resubmitted${newId ? ` → ${newId.slice(0, 8)}…` : ""}`;
      setCancelFeedback({ type: "success", message });
      await fetchData();
      if (newId) navigate(`/celery/${newId}`);
      return { ok: true, newTaskId: newId, message };
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      const message = `Rerun failed: ${detail}`;
      setCancelFeedback({ type: "error", message });
      return { ok: false, message };
    } finally {
      setRerunInFlightTaskId(null);
    }
  }, [fetchData, navigate]);

  useEffect(() => {
    if (!taskId) {
      setDrawerOpen(false);
      setSelectedTask(null);
      return;
    }

    const existing = tasks.find((task) => task.id === taskId);
    if (existing) {
      setSelectedTask(existing);
      setDrawerOpen(true);
      return;
    }

    let cancelled = false;
    celeryApi.getTask(taskId)
      .then((response) => {
        if (cancelled) return;
        setSelectedTask(response.data);
        setDrawerOpen(true);
      })
      .catch(() => {
        if (cancelled) return;
        setError(`Task ${taskId} not found.`);
      });

    return () => {
      cancelled = true;
    };
  }, [taskId, tasks]);

  useEffect(() => {
    if (!selectedTask?.id) return;
    const tracked = cancelRequests[selectedTask.id];
    if (!tracked) return;

    const latestTask = tasks.find((task) => task.id === selectedTask.id) || selectedTask;
    const state = (latestTask.state || "").toUpperCase();
    if (TERMINAL_STATES.has(state)) {
      setCancelFeedback({
        type: "success",
        message: `Task ${selectedTask.id.slice(0, 8)}… is now ${state}.`,
      });
      return;
    }

    setCancelFeedback({ type: "success", message: tracked.message });
  }, [cancelRequests, selectedTask?.id, selectedTask?.state, tasks]);

  useEffect(() => {
    if (!drawerOpen || !selectedTask?.id) return;

    const state = (selectedTask.state || "").toUpperCase();
    const pollableStates = new Set(["STARTED"]);
    if (!pollableStates.has(state)) return;

    let stopped = false;

    const pollTaskDetails = async () => {
      try {
        const response = await celeryApi.getTask(selectedTask.id);
        if (stopped) return;
        const updatedTask = response.data;
        setSelectedTask(updatedTask);
        setTasks((previousTasks) =>
          previousTasks.map((task) =>
            task.id === updatedTask.id ? { ...task, ...updatedTask } : task,
          ),
        );
      } catch {
      }
    };

    pollTaskDetails();
    const intervalId = setInterval(pollTaskDetails, 10_000);

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [drawerOpen, selectedTask?.id, selectedTask?.state]);

  return (
    <CeleryPage
      stats={stats}
      tasks={tasks}
      workers={workers}
      loading={loading}
      error={error}
      tab={tab}
      search={search}
      selectedTask={selectedTask}
      drawerOpen={drawerOpen}
      cancelInFlightTaskId={cancelInFlightTaskId}
      cancelFeedback={cancelFeedback}
      stateColours={STATE_COLOURS}
      onTabChange={setTab}
      onSearchChange={setSearch}
      onRefresh={fetchData}
      onOpenDetail={openDetail}
      onCloseDetail={closeDrawer}
      onCancelTask={cancelTask}
      onRerunTask={rerunTask}
      rerunInFlightTaskId={rerunInFlightTaskId}
    />
  );
};

export default CeleryMonitorContainer;
